import React, { useState, useEffect, useRef } from 'react'
import { GlobalLoader, DateBasedChart, XYChart, Breadcrumbs, FlashMessage, DropDownMenu, DynamicEntityIndex, RotatingGlobeChart, NoData, DataCard, SelectInput } from '@reactiveonline/reactive_admin_shared-packages'
import 'react-dates/initialize'
import moment from 'moment'
import { truncateString } from '@reactiveonline/reactive_admin_shared-packages/utils'
import PresetDateRangePicker from './PresetDateRangePicker'

export default function Report({
  report, chartColors, breadcrumbs, exportReportPath, allowFutureDates,
  filters, initialFilters, allowSearch, pastDaysLimit, hideDateRangePicker, maxDateRangeLimit, actions, isMonthlyDateRange, tenantReportPath
}) {
  const MAX_DATE_RANGE_DAYS = maxDateRangeLimit || 92
  const DEFAULT_INITIAL_DAYS = 30
  let initDays = report.initialDates !== undefined ? report.initialDates : DEFAULT_INITIAL_DAYS
  if (maxDateRangeLimit && maxDateRangeLimit < initDays) {
    initDays = maxDateRangeLimit
  }
  const [dateRange, setDateRange] = useState({
    start_date: moment().subtract(initDays, 'days'),
    end_date: moment(new Date())
  })
  const [showFilters, setShowFilters] = useState(false)
  const [currentFilters, setCurrentFilters] = useState(initialFilters || {})
  const [chartData, setChartData] = useState([])
  const [completeListData, setCompleteListData] = useState([])
  const [listData, setListData] = useState([])
  const [listTotalPages, setListTotalPages] = useState(1)
  const [cardData, setCardData] = useState([])
  const [loading, setLoading] = useState(false)
  const [toggledItems, setToggledItems] = useState([])
  const [renderKey, setRenderKey] = useState(1)
  const [actionsState, setActionsState] = useState(actions)

  const flashMessageRef = useRef(null)
  const isMobileApp = window.location.search.includes('origin=mobileApp')

  useEffect(()=> {
    fetchReportData()
  }, [currentFilters])

  useEffect(() => {
    if (actions) {
      setActionsState(prevState => {
        return prevState.reduce((acc, curr) => {
          if (curr.icon === 'fa-chart-line-up') {
            acc.push({
              ...curr,
              url: item => `${ tenantReportPath }${ item.account_uid }?start_date=${ Date.parse(dateRange.start_date.format('YYYY-MM-DD')) }&end_date=${ Date.parse(dateRange.end_date.format('YYYY-MM-DD')) }`
            })
          } else {
            acc.push(curr)
          }

          return acc
        }, [])
      })
    }

    setRenderKey(Math.random())
  }, [dateRange])

  function fetchReportData(range = dateRange){
    setLoading(true)
    let url = `${report.fetchDataPath}?date_range=${JSON.stringify(range)}`

    Object.keys(currentFilters).forEach( filter => (
      url += `&${ filter }=${currentFilters[filter]}`
    ))

    Rails.ajax({
      type: 'GET',
      url: url,
      dataType: 'json',
      success: res => {
        setListTotalPages(res.totalPages)
        setListData(res.items)
        setToggledItems([])
        setCompleteListData(res.completeItems || [])

        if (res.cardData){
          setCardData(res.cardData)
        }

        if (report.type === "date_based"){
          setChartData(res.data.map(item => ({
            date: item.date,
            value: item[report.yAxis]
          })))
        } else if (report.type === "bar"){
          if (['product_name', "product", "customer", "method_name", "store", "company", "status", "tenant_name"].includes(report.xAxis)){
            let data = res.data
            let xValues = []
            const finalData = data.map(item => {
              let xValue = truncateString(item[report.xAxis], 20)
              while (xValues.includes(xValue)){
                xValue = xValue + " "
              }
              xValues.push(xValue)
              return({
                category: xValue,
                [report.yAxis]: item[report.yAxis]
              })
            })
            setChartData(finalData)
          } else {
            setChartData(res.data.map(item => ({
              category: item[report.xAxis],
              [report.yAxis]: item[report.yAxis]
            })))
          }
        } else {
          setChartData(res.data.map(item => ({
            id: item.alpha2_code,
            name: item.location,
            value: item[report.valueField],
            polygonTemplate: { fill: chartColors[0] }
          })))
        }
      },
      complete: res => {
        setLoading(false)
      }
    })
  }

  function handleToggleItem(itemId, nestedItems){
    if (toggledItems.includes(itemId)){
      setToggledItems(itemIds => itemIds.filter(id => id !== itemId))
      const idsToBeRemoved = nestedItems.map(item => item.id)
      setListData(listItems => listItems.filter(item => !idsToBeRemoved.includes(item.id)))
    } else {
      setToggledItems(itemIds => [...itemIds, itemId])
      const toggledItemIndex = listData.findIndex(dataItem => dataItem.id === itemId)
      let updatedListData = [...listData]
      updatedListData.splice(toggledItemIndex + 1, 0, ...nestedItems)
      setListData(updatedListData)
    }
  }

  function itemRenderer(item, columns) {
    return (
      <>
        { columns.map( column => {
            const truncatableColumn = ['product_name', "product", "customer", "method_name", "store", "company", "status", "tenant_name"].includes(column.label)
            return(
              <div key={ column.label } className='item flex-1'>
                { report.nested_data && report.first_column === column.label && item[report.nested_data] && item[report.nested_data].length > 0 &&
                  <i className={ `fa-light fa-chevron-${ toggledItems.includes(item.id) ? 'up' : 'right' } m-r-10 color-scheme cursor-pointer`} onClick={() => handleToggleItem(item.id, item[report.nested_data])}/>
                }
                { truncatableColumn ? truncateString(item[column.label], 20) : item[column.label] }
              </div>
            )
        })}
      </>
    )
  }

  function handleExportReport(){
    let fd = new FormData()
    fd.append('list_data', JSON.stringify(completeListData))
    fd.append('start_date', dateRange.start_date.format('DD/MM/YYYY'))
    fd.append('end_date', dateRange.end_date.format('DD/MM/YYYY'))
    fd.append('date_range', JSON.stringify(dateRange))
    fd.append('filters', JSON.stringify(currentFilters))

    Rails.ajax({
      type: 'POST',
      data: fd,
      url: exportReportPath,
      dataType: 'json',
      success: res => {
        flashMessageRef.current.show(ReactiveCore.translations.entityInvoices.exportStarted, 'success')
      },
      error: res => {

      }
    })
  }

  function renderFilters() {
    return (
      <>
        { filters.map( (filter, index) => (
          <div key={ index } className={ `filter-item ${ filter.type === 'radio' ? 'date-range' : '' }` }>
            <div className='filter-name'>
              { filter.title }
            </div>

            <div className='filter-action'>
              { filter.type === 'select' &&
                <SelectInput
                  containerClasses='flex-1'
                  style={{ width: '100%' }}
                  options={ [{ value: 'all', title: ReactiveCore.translations.all }, ...filter.options] }
                  currentOption={ currentFilters[filter.key] || 'all' }
                  setCurrentOption={ option => setCurrentFilters({ [filter.key]: option.value }) }
                />
              }

              { filter.type === 'multiSelect' &&
                <SelectInput
                  containerClasses='flex-1'
                  style={{ width: '100%' }}
                  options={ [{ value: 'all', title: ReactiveCore.translations.all }, ...filter.options] }
                  currentOption={ currentFilters[filter.key] || ['all'] }
                  setCurrentOption={ options => handleFilterMultiSelect(options, filter.key) }
                  multiple
                />
              }

              { filter.type === 'radio' && filter.options.map( (option, index) => (
                <div key={ index } className='date-range-radios'>
                  <input
                    type='radio'
                    value={ option.value }
                    checked={ (!currentFilters[filter.key] && index === 0) || (currentFilters[filter.key] === option.value) }
                    onChange={ event => setCurrentFilters({ [filter.key]: event.target.value }) }
                  />
                  <div className='radio-label m-l-5'>
                    { option.title }
                  </div>
                </div>
              ))}

              { filter.type === 'toggle' &&
                <div className="flex-1 flex-box content-end">
                  <Toggle
                    icons={{ unchecked: null }}
                    className='reactToggle-element small'
                    checked={ currentFilters[filter.key] === true }
                    onChange={ (event) => {
                      setCurrentFilters({ [filter.key]: event.target.checked })
                    }}
                  />
                </div>
              }

            </div>
          </div>
        )) }
      </>
    )
  }

  function handleApplyDateRange(updatedDateRange) {
    setDateRange(updatedDateRange)
    fetchReportData(updatedDateRange)
  }

  return(
    <>
      <div className='page-wrapper-header flex-box content-space-between'>
        <div className='page-header'>
          <h1 className='page-header-title'>
            { report.title }
          </h1>
        </div>
        <div className='flex-box items-center'>
          <div style={{ marginRight: '15px' }}>
            { !hideDateRangePicker &&
              <PresetDateRangePicker
                value={ dateRange }
                onApply={ handleApplyDateRange }
                allowFutureDates={ allowFutureDates }
                pastDaysLimit={ pastDaysLimit }
                maxDateRangeDays={ MAX_DATE_RANGE_DAYS }
                defaultPresetDays={ report.initialDates !== undefined ? report.initialDates : 30 }
              />
            }
          </div>

          { filters &&
            <div className='advanced-filters-wrapper m-r-10' style={{ position: 'relative' }}>
              { showFilters && <div className='advanced-filters-overlay' onClick={ ()=> setShowFilters(false) }></div> }
              <div
                id='advanced-filters-button'
                className='flex-box items-center'
                style={{ cursor: 'pointer' }}
                onClick={ () => { if (!loading) setShowFilters(!showFilters) }}
              >
                <i className='fa-light fa-bars-filter m-r-5'/>
                { ReactiveCore.translations.filters }
              </div>

              { showFilters &&
                <div className='advanced-filters-drop-menu'>
                  <div className='filter-item-wrapper'>
                    <div className='filter-title'>
                      <b>{ ReactiveCore.translations.filters }:</b>
                    </div>

                    { renderFilters() }
                  </div>
                </div>
              }
            </div>
          }

          <DropDownMenu
            items={ [{ icon: 'fa-file-excel', action: handleExportReport, text: ReactiveCore.translations.reports.export_xlsx, permissions: true }] }
            title={ ReactiveCore.translations.dropMenuAction }
          />
        </div>
      </div>

      { !isMobileApp &&
        <Breadcrumbs
          breadcrumbs={ breadcrumbs }
        />
      }

      <>
        { chartData.length > 0 &&
          <div className='card'>
            <div className='card-content'>
              <div className='card-body'>

                { report.type === "date_based" &&
                  <DateBasedChart
                    data={ chartData }
                    chartKey={ 'chartdiv1' }
                  />
                }

                { report.type === "bar" &&
                  <XYChart
                    data={ chartData }
                    seriesLabels={ [{ [report.yAxis]: report.seriesLabel }] }
                    xAxisTitle={ '' }
                    yAxisTitle={ '' }
                    columnWidth={ 15 }
                    chartColors={ chartColors }
                    chartKey={ "chartdiv1" }
                  />
                }

                { report.type === "rotating_globe" &&
                  <div className='flex-1'>
                    <RotatingGlobeChart
                      data= { chartData }
                      height={ '300px' }
                      chartKey='chartdiv3'
                      chartColors={ chartColors }
                    />
                  </div>
                }
              </div>
            </div>
          </div>
        }

        { cardData && cardData.length > 0 &&
          <div className='flex-box m-b-15'>
            { cardData.map((cardInfo, index) =>
              <div key={ index } className={`${index === cardData.length - 1 ? '' : 'm-r-10'} flex-1`}>
                <DataCard
                  title={ cardInfo.title }
                  data={ cardInfo.data }
                />
              </div>
            )}
          </div>
        }

        { (listData.length > 0 || allowSearch) ?
          <DynamicEntityIndex
            key={ renderKey }
            initialItems={ listData }
            setInitialItems={ setListData }
            initialTotalPages={ listTotalPages }
            itemsPath={ report.fetchDataPath }
            tabs={ [] }
            tableHeadings={ report.columns }
            itemRenderer={ itemRenderer }
            urlParams={ `date_range=${JSON.stringify(dateRange)}` }
            noResourcesTitle={ ReactiveCore.translations.analytics.noData }
            icon={ 'fa-chart-scatter-bubble' }
            disableDelete
            disableSearch={ !allowSearch }
            disableReload
            disableUrlUpdates
            actions={ actionsState ?? [] }
          />
          :
          <div className='card'>
            <div className='card-content'>
              <div className='card-body'>
                <NoData />
              </div>
            </div>
          </div>
        }
      </>

      { loading && <GlobalLoader fullPage/> }

      <FlashMessage ref={ flashMessageRef } />
    </>
  )
}
